import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteConfig, tickerItems } from '@/mocks/site';
import { articles } from '@/mocks/articles';

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setQuery('');
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (searchOpen) {
      searchRef.current?.focus();
    }
  }, [searchOpen]);

  const keyword = query.trim();
  const results = keyword
    ? articles
        .filter(
          (a) =>
            a.title.includes(keyword) ||
            a.categoryName.includes(keyword) ||
            a.tags.some((t) => t.includes(keyword)),
        )
        .slice(0, 5)
    : [];

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-foreground-950 text-background-50">
        <div className="px-4 md:px-8 h-9 flex items-center gap-3 overflow-hidden">
          <span className="shrink-0 flex items-center gap-1.5 text-xs font-bold text-accent-400">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse-dot"></span>
            独家快讯
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex whitespace-nowrap animate-ticker">
              {[...tickerItems, ...tickerItems].map((item, i) => (
                <span key={`${item}-${i}`} className="text-xs text-background-200 mr-10 flex items-center gap-2">
                  <i className="ri-flashlight-fill text-primary-400"></i>
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="px-4 md:px-8 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer" aria-label="17爆料网首页">
            <span className="w-9 h-9 rounded-md bg-accent-600 text-background-50 flex items-center justify-center font-heading font-bold text-lg">
              17
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-lg text-foreground-950 tracking-tight">爆料网</span>
              <span className="text-[10px] text-foreground-500 tracking-widest font-label">BAOLIAO WANG</span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden lg:flex items-center gap-1 ml-4">
            {siteConfig.nav.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="px-3 py-2 text-sm text-foreground-700 rounded-md hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:block relative">
              <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-background-300 bg-background-100 focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索爆料线索"
                  className="w-40 xl:w-52 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                  aria-label="搜索爆料线索"
                />
              </div>
              {keyword && (
                <div className="absolute right-0 top-11 w-80 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  {results.length ? (
                    results.map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.id}`}
                        className="block px-4 py-3 hover:bg-background-100 border-b border-background-100 last:border-0 cursor-pointer"
                      >
                        <span className="block text-sm text-foreground-900 line-clamp-1">{a.title}</span>
                        <span className="block text-xs text-foreground-500 mt-1">{a.categoryName}</span>
                      </Link>
                    ))
                  ) : (
                    <p className="px-4 py-4 text-sm text-foreground-500">没有找到相关线索，试试其他关键词。</p>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen((v) => !v)}
              className="md:hidden w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 cursor-pointer"
            >
              <i className="ri-search-line"></i>
            </button>

            <Link
              to="/contact"
              className="hidden sm:flex items-center gap-1.5 h-9 px-4 rounded-md bg-accent-600 text-background-50 text-sm font-medium hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-megaphone-line"></i>
              我要爆料
            </Link>

            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-lg' : 'ri-menu-line text-lg'}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="md:hidden px-4 pb-3">
            <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-background-300 bg-background-100">
              <i className="ri-search-line text-foreground-400"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索爆料线索"
                className="flex-1 bg-transparent text-sm outline-none"
                aria-label="搜索爆料线索"
              />
            </div>
            {keyword && (
              <div className="mt-2 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                {results.length ? (
                  results.map((a) => (
                    <Link key={a.id} to={`/article/${a.id}`} className="block px-4 py-3 border-b border-background-100 last:border-0 cursor-pointer">
                      <span className="block text-sm text-foreground-900 line-clamp-1">{a.title}</span>
                    </Link>
                  ))
                ) : (
                  <p className="px-4 py-4 text-sm text-foreground-500">没有找到相关线索。</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-b border-background-200 px-4 py-3">
          <nav aria-label="移动端导航" className="flex flex-col">
            {siteConfig.nav.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="py-3 text-sm text-foreground-800 border-b border-background-100 last:border-0 cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}